import * as _ from 'lodash';

import {
  AddUserDialogComponent,
  DeleteUserDialogComponent,
} from '../../dialogs';
import { App, AppUserResult } from 'app/models';
import {
  AppUsersService,
  AppsService,
  SnackBarService,
  UsersSearchService,
  UsersService,
} from 'app/services';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { SnackbarType } from 'app/constants/Enums';
import { UserDialogComponent } from '../../users/user-dialog/user-dialog.component';
import * as tableHelpers from 'app/helpers/tableSelectionHelpers';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';

@Component({
  standalone: false,
  selector: 'app-app-users',
  templateUrl: './apps-users.component.html',
  styleUrls: ['./apps-users.component.scss'],
})
export class AppsUsersComponent implements OnInit, OnDestroy {
  displayedColumns = ['select', 'name', 'email', 'roleString'];

  private onDestroy$ = new Subject<void>();
  private searchValSubject = new BehaviorSubject<string>('');
  @Input()
  app: App;
  @Input()
  image: string;
  @Input()
  rolesSub: Subject<any[]>;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  roles: any[];
  rolesStr = '';
  clientId: string;
  displayUsers: any;
  error: string;
  appOwner: any;
  apps: App[];
  appUserObj: AppUserResult;
  pageIndex = 0;
  pageSize = 10;
  showFilter: boolean;
  filter: string;
  searchVal$ = this.searchValSubject.asObservable();
  queryString ='';
  message = 'No_Users_Found';
  currentProfile: any;
  currentPageUsers: any[];
  selectedUsers = new SelectionModel<any>(true, []);
  userListDataSource: MatTableDataSource<any>;

  constructor(
    private route: ActivatedRoute,
    public _appsService: AppsService,
    public _appUserServ: AppUsersService,
    public dialog: MatDialog,
    public _userSearhServ: UsersSearchService,
    private readonly usersService: UsersService,
    public dectChg: ChangeDetectorRef,
    private _snackbarService: SnackBarService,
    private _translateService: TranslateService
  ) {
    this._appUserServ.loading = false;
  }

  ngOnInit() {
    this.clientId = this.route.snapshot.params['client_id'];
    this.subscribeToAppUsers();
    this.getData(this.clientId);
    this.displayUsers = [];
    this.getApps();
    this.searchVal$
      .pipe(takeUntil(this.onDestroy$), debounceTime(600))
      .subscribe((res) => {
        if (res !== '' && res != null) {
          const encodedSearch = encodeURIComponent(res);
          this._appUserServ.searchAppUsers(encodedSearch, this.clientId, true);
        }
      });
    this.usersService.user$.pipe(take(1)).subscribe((res) => {
      this.currentProfile = res;
    });
    this.rolesSub.pipe(takeUntil(this.onDestroy$)).subscribe((roles) => {
      this.roles = JSON.parse(JSON.stringify(roles));
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next(null);
  }

  close(event) {
    this.showFilter = false;
  }

  showFilters() {
    this.showFilter = this.roles.some(x => x.isSelected);
  }

  private _parseName() {
    if (!this._appsService.selectedApp) {
      return;
    }
    this._appsService.userInitials = `${this._appsService.selectedApp.name[0].toUpperCase()}${this._appsService.selectedApp.name[
      this._appsService.selectedApp.name.length - 1
    ].toUpperCase()}`;
  }

  getApps() {
    this._appsService.apps$.pipe(takeUntil(this.onDestroy$)).subscribe(
      (apps) => {
        this._appsService.selectedApp = apps.find(
          (app) => app.clientId === this.clientId
        );
        this._parseName();
      },
      (error) => (this.error = error)
    );
  }

  monitorSearch(event) {
    this.queryString = event.trim();
    if (this.queryString.length >= Number(environment.minimumSearchLength)) {
      this.clearFilter();
      this.paginator.firstPage();
      this.searchValSubject.next(this.queryString.trim());
    } else if (this.queryString == null || this.queryString === '') {
      this._appUserServ.noRecordFound = false;
      this.paginator.firstPage();
      this.searchValSubject.next('');
      this.getData(this.clientId);
    }
  }

  selectedRecords(user) {
    this.selectedUsers.toggle(user);
  }

  getData(clientId) {
    this._appUserServ.loading = true;
    this._appUserServ.getAppUsers(clientId, 0, this.pageSize, this.rolesStr);
  }

  goToAddUsers() {
    const isInternal = this._appsService.selectedApp.clientMetadata
      ? this._appsService.selectedApp.clientMetadata.internalOnly
      : false;
    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      disableClose: true,
      autoFocus: false,
      width: '800px',
      height: '500px',
      data: {
        clientId: this.clientId,
        app: this._appsService.selectedApp,
        isInternal,
      },
    });
  }

  goToDeleteUsers() {
    let title = '';
    let subTitle = '';
    const userLength = this.selectedUsers.hasValue()
      ? this.selectedUsers.selected.length
      : 0;
    if (userLength === 0) {
      this._translateService
        .get('SNACKBAR.remove_user_roles')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(value => (title = value));
      this._translateService
        .get('SNACKBAR.no_user_selected')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(value => (subTitle = value));
      this._snackbarService.showSnackbar(
        SnackbarType.info,
        title,
        subTitle
      );
      return;
    }
    const isSameUser =
      this.selectedUsers.selected.filter(
        (e) => e.id === this.currentProfile.id
      ).length > 0;
    if (isSameUser) {
      this._translateService
        .get('SNACKBAR.warning')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(value => (title = value));
      this._translateService
        .get('SNACKBAR.delete_own')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(value => (subTitle = value));
      this._snackbarService.showSnackbar(
        SnackbarType.warning,
        title,
        subTitle
      );
      return;
    }
    const dialogRef = this.dialog.open(DeleteUserDialogComponent, {
      disableClose: true,
      autoFocus: false,
      width: '30vw',
      height: '30vw',
      data: {
        users: this.selectedUsers.selected,
        appName: this._appsService.selectedApp.name
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        let ids = [];
        ids = this.selectedUsers.selected
          .filter((e) => e.id)
          .map((elem) => elem.id);
        this._appUserServ.deleteUserRoles(ids, this.clientId);
        this.selectedUsers.clear();
      }
    });
  }

  filterData(role) {
    role.isSelected = !role.isSelected;
    this.showFilters();
    this.rolesStr = this.roles
      .filter((e) => e.isSelected)
      .map((elem) => elem.role)
      .join(',');

    this.queryString = '';
    this.searchValSubject.next('');

    this.paginator.firstPage();
    this.selectedUsers.clear();
    this._appUserServ.getAppUsers(
      this.clientId,
      0,
      this.pageSize,
      this.rolesStr
    );
  }

  openUserProfile(selectedUser) {
    const dialog = this.dialog.open(UserDialogComponent, {
      disableClose: true,
      panelClass: 'no-padding-dialog',
      autoFocus: false,
      width: '880px',
      data: {
        user: selectedUser,
        clientId: this.clientId,
      },
    });
  }

  selectedRecord(user) {
    this.openUserProfile(user);
  }

  subscribeToAppUsers(): void {
    this._appUserServ.appUsers$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((appUserObj) => {
        if (appUserObj) {
          this.displayUsers = {
            data: appUserObj.users,
            total: appUserObj.appUserCount,
            page: this.pageIndex,
          };
          this.userListDataSource = new MatTableDataSource(appUserObj.users);
          if (this.displayUsers.data?.length === this.displayUsers.total) {
            this.userListDataSource.paginator = this.paginator;
          }
          this.userListDataSource
            .connect()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((users) => {
              this.currentPageUsers = users;
            });
          this._appUserServ.loading = false;
          this.appUserObj = appUserObj;
          this._appUserServ.noRecordFound = appUserObj.users.length === 0;
          this.dectChg.detectChanges();
        }
      });
  }

  getPagination(event) {
    if (this.displayUsers.data?.length !== this.displayUsers.total) {
      this.selectedUsers.clear();
      if (event?.pageSize) {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
      }
      this._appUserServ.loading = true;
      this._appUserServ.getAppUsers(
        this.clientId,
        this.pageIndex,
        this.pageSize,
        this.rolesStr
      );
    }
  }

  clearFilter() {
    this.rolesStr = '';
    this.showFilter = false;
    this.roles.forEach((role) => {
      role.isSelected = false;
    });
    this.selectedUsers.clear();
    this.paginator.firstPage();
  }

  clearAllFilters() {
    this.clearFilter();
    this.getData(this.clientId);
  }

  getSelectedRoles() {
    return this.roles?.filter(x=>x.isSelected);
  }

  isAllSelected = () => tableHelpers.isAllSelected(this.currentPageUsers, this.selectedUsers.selected);
  masterToggle = () => tableHelpers.masterToggle(this.currentPageUsers, this.selectedUsers);
  isPartiallySelected = () => tableHelpers.isPartiallySelected(this.currentPageUsers, this.selectedUsers.selected);

}
